import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState, ReactNode } from 'react';
import { HEADING_FONT_CSS, SCROLLBAR_CSS, TABLE_STICKY_BG_CSS } from './vendoiqTheme';

type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'viq-admin.theme-mode';

function initialMode(): ThemeMode {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

interface ThemeModeContextValue {
  mode: ThemeMode;
  toggle: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(initialMode);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  // Stamp the active mode onto <html> so global CSS (SCROLLBAR_CSS) can theme
  // native scrollbars — antd's algorithm can't reach them. useLayoutEffect
  // lands it before paint, avoiding a flash of the wrong-colored scrollbar.
  useLayoutEffect(() => {
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  const toggle = useCallback(() => {
    setMode((m) => (m === 'light' ? 'dark' : 'light'));
  }, []);

  const value = useMemo(() => ({ mode, toggle }), [mode, toggle]);

  return (
    <ThemeModeContext.Provider value={value}>
      {/* Mode-independent by design (font-family doesn't vary light/dark) — see
          HEADING_FONT_CSS's doc comment for why this is a plain style rule
          rather than an antd theme token. Both SPAs mount this provider once
          at the root, so this is the single injection point for both apps. */}
      <style id="vendoiq-heading-font">{HEADING_FONT_CSS}</style>
      <style id="vendoiq-scrollbars">{SCROLLBAR_CSS}</style>
      {/* Batch leads table only (`.viq-batch-leads-table` — see
          TABLE_STICKY_BG_CSS's doc comment): antd's sticky-header holder and
          horizontal scroll-holder paint with container-level tokens instead
          of the table's own `headerBg`, showing a lighter band around the
          fixed Name/Captured columns on scroll. */}
      <style id="vendoiq-table-sticky-bg">{TABLE_STICKY_BG_CSS}</style>
      {children}
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode(): ThemeModeContextValue {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error('useThemeMode must be used inside <ThemeModeProvider>');
  return ctx;
}
